import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  AddListToCartResult,
  InventoryAvailabilityMap,
  NestedCartInstance,
  ShoppingList,
  ShoppingListItem,
  ShoppingListWritePayload,
  SupermarketCatalogProduct,
  SHOPPING_LIST_MAX_ITEMS,
} from '../types/shoppingList';
import { buildCartProductsFromList, mergeCartProducts, mergeListItems } from '../utils/shoppingListUtils';

export const SHOPPING_LISTS_COLLECTION = 'ShoppingLists';

const FIRESTORE_IN_QUERY_LIMIT = 10;

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

export async function createShoppingList(payload: ShoppingListWritePayload): Promise<string> {
  const docRef = await addDoc(collection(db, SHOPPING_LISTS_COLLECTION), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateShoppingList(
  listId: string,
  updates: Partial<ShoppingListWritePayload>
): Promise<void> {
  await updateDoc(doc(db, SHOPPING_LISTS_COLLECTION, listId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteShoppingList(listId: string): Promise<void> {
  await deleteDoc(doc(db, SHOPPING_LISTS_COLLECTION, listId));
}

/**
 * Live subscription to the current user's shopping lists, newest first.
 * Sorting is done client-side so no composite Firestore index is required.
 */
export function subscribeToUserShoppingLists(
  userId: string,
  onLists: (lists: ShoppingList[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const listsQuery = query(
    collection(db, SHOPPING_LISTS_COLLECTION),
    where('userId', '==', userId)
  );
  return onSnapshot(
    listsQuery,
    (snapshot) => {
      const lists = snapshot.docs.map((listDoc) => ({
        id: listDoc.id,
        ...(listDoc.data() as Omit<ShoppingList, 'id'>),
      }));
      lists.sort(
        (a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)
      );
      onLists(lists);
    },
    (error) => onError(error)
  );
}

/**
 * Fetch the full inventory for a supermarket and index it by product id,
 * so availability checks for a list need a single query per supermarket.
 */
export async function getSupermarketInventoryMap(
  supermarketId: string
): Promise<InventoryAvailabilityMap> {
  const inventoryQuery = query(
    collection(db, 'Inventory'),
    where('supermarket_id', '==', supermarketId)
  );
  const snapshot = await getDocs(inventoryQuery);
  const inventoryMap: InventoryAvailabilityMap = {};
  snapshot.forEach((inventoryDoc) => {
    const data = inventoryDoc.data();
    if (!data.product_id) return;
    const availableQuantity = typeof data.quantity === 'number' ? data.quantity : 0;
    inventoryMap[data.product_id] = {
      inStock: availableQuantity > 0,
      availableQuantity,
      salePrice: typeof data.sale_price === 'number' ? data.sale_price : null,
    };
  });
  return inventoryMap;
}

/**
 * In-stock products at a supermarket, joined with product display names.
 * Used when adding items to a shopping list from that store's catalog.
 */
export async function fetchSupermarketCatalogProducts(
  supermarketId: string
): Promise<SupermarketCatalogProduct[]> {
  const inventoryQuery = query(
    collection(db, 'Inventory'),
    where('supermarket_id', '==', supermarketId)
  );
  const snapshot = await getDocs(inventoryQuery);

  const inventoryRows: Array<{
    productId: string;
    availableQuantity: number;
    salePrice: number | null;
  }> = [];

  snapshot.forEach((inventoryDoc) => {
    const data = inventoryDoc.data();
    if (!data.product_id) return;
    const availableQuantity = typeof data.quantity === 'number' ? data.quantity : 0;
    if (availableQuantity <= 0) return;
    inventoryRows.push({
      productId: data.product_id,
      availableQuantity,
      salePrice: typeof data.sale_price === 'number' ? data.sale_price : null,
    });
  });

  if (inventoryRows.length === 0) {
    return [];
  }

  const productIds = Array.from(new Set(inventoryRows.map((row) => row.productId)));
  const productDataById = new Map<string, Record<string, unknown>>();

  for (const idChunk of chunkArray(productIds, FIRESTORE_IN_QUERY_LIMIT)) {
    const productsQuery = query(collection(db, 'Products'), where(documentId(), 'in', idChunk));
    const productsSnapshot = await getDocs(productsQuery);
    productsSnapshot.forEach((productDoc) => {
      productDataById.set(productDoc.id, productDoc.data());
    });
  }

  const products: SupermarketCatalogProduct[] = [];

  inventoryRows.forEach((row) => {
    const productData = productDataById.get(row.productId);
    if (!productData) return;

    const salePrice =
      row.salePrice !== null
        ? row.salePrice
        : typeof productData.price === 'number'
          ? productData.price
          : 0;

    products.push({
      productId: row.productId,
      name: (productData.display_name as string) || 'Unnamed Product',
      price: salePrice,
      category: (productData.category as string) || 'Uncategorized',
      availableQuantity: row.availableQuantity,
    });
  });

  products.sort((a, b) => a.name.localeCompare(b.name));
  return products;
}

/**
 * One-shot fetch of the user's lists for a supermarket (client-filtered).
 * Uses a userId-only query to avoid a composite index; if users accumulate many
 * lists across stores, consider `where('userId','==',…).where('supermarketId','==',…)`.
 */
export async function getUserShoppingListsForSupermarket(
  userId: string,
  supermarketId: string
): Promise<ShoppingList[]> {
  const listsQuery = query(
    collection(db, SHOPPING_LISTS_COLLECTION),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(listsQuery);
  return snapshot.docs
    .map((listDoc) => ({
      id: listDoc.id,
      ...(listDoc.data() as Omit<ShoppingList, 'id'>),
    }))
    .filter((list) => list.supermarketId === supermarketId)
    .sort((a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0));
}

/**
 * Append (or increment) a product on an existing shopping list.
 * Reads the latest list doc inside a transaction to avoid stale overwrites.
 * @throws when the merged list would exceed {@link SHOPPING_LIST_MAX_ITEMS} lines.
 */
export async function addProductToShoppingList(
  listId: string,
  product: ShoppingListItem
): Promise<void> {
  const listRef = doc(db, SHOPPING_LISTS_COLLECTION, listId);

  await runTransaction(db, async (transaction) => {
    const listDoc = await transaction.get(listRef);
    if (!listDoc.exists()) {
      throw new Error('SHOPPING_LIST_NOT_FOUND');
    }

    const listData = listDoc.data() as Omit<ShoppingList, 'id'>;
    const mergedItems = mergeListItems(listData.items ?? [], [product]);
    if (mergedItems.length > SHOPPING_LIST_MAX_ITEMS) {
      throw new Error('SHOPPING_LIST_ITEM_LIMIT');
    }

    transaction.update(listRef, {
      items: mergedItems,
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Create (or merge into) the user's active cart for the list's supermarket,
 * adding only the products that are currently in stock.
 */
export async function addShoppingListToCart(
  userId: string,
  list: ShoppingList,
  inventoryMap: InventoryAvailabilityMap
): Promise<AddListToCartResult> {
  const { products, skippedProductNames, reducedQuantityNames } = buildCartProductsFromList(
    list.items,
    inventoryMap
  );

  const result: AddListToCartResult = {
    totalProducts: list.items.length,
    addedProducts: products.length,
    skippedProductNames,
    reducedQuantityNames,
    cartInstanceId: null,
  };

  if (products.length === 0) {
    return result;
  }

  const cartRef = doc(db, 'Carts', userId);
  await runTransaction(db, async (transaction) => {
    const cartDoc = await transaction.get(cartRef);
    const cartData = cartDoc.exists() ? cartDoc.data() : {};
    const supermarketCartInstances = cartData[list.supermarketId];

    let activeCartInstanceId: string | null = null;
    let activeCartInstance: NestedCartInstance | null = null;
    if (
      supermarketCartInstances &&
      typeof supermarketCartInstances === 'object' &&
      !Array.isArray(supermarketCartInstances)
    ) {
      for (const instanceId of Object.keys(supermarketCartInstances)) {
        const candidate = supermarketCartInstances[instanceId];
        if (candidate && candidate.status === 'active') {
          activeCartInstanceId = instanceId;
          activeCartInstance = candidate as NestedCartInstance;
          break;
        }
      }
    }

    if (activeCartInstance) {
      activeCartInstance.products = mergeCartProducts(
        activeCartInstance.products || [],
        products,
        inventoryMap
      );
      activeCartInstance.lastUpdatedAt = serverTimestamp();
      activeCartInstance.hasUnseenUpdate = true;
    } else {
      activeCartInstanceId = `cart_instance_${Date.now()}`;
      activeCartInstance = {
        instanceId: activeCartInstanceId,
        products,
        status: 'active',
        supermarketName: list.supermarketName,
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        hasUnseenUpdate: true,
      };
    }

    // Field-path update: only touch this supermarket's instance, so concurrent
    // writes to other supermarkets or instances are not overwritten.
    if (cartDoc.exists()) {
      transaction.update(cartRef, {
        [`${list.supermarketId}.${activeCartInstanceId}`]: activeCartInstance,
        lastGlobalUpdate: serverTimestamp(),
      });
    } else {
      transaction.set(cartRef, {
        [list.supermarketId]: { [activeCartInstanceId as string]: activeCartInstance },
        lastGlobalUpdate: serverTimestamp(),
      });
    }

    result.cartInstanceId = activeCartInstanceId;
  });

  return result;
}
